import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Building,
  Mail,
  Phone,
  FileText,
  ChevronDown,
  ChevronUp,
  Filter,
  Download,
  LogOut,
  Moon,
  Sun,
  Settings
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useNavigate, Link } from 'react-router-dom';
import { useWorkdayClientTheme } from '../../contexts/WorkdayClientThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import WorkdayClientHeader from '../../components/workday-client/WorkdayClientHeader';

interface InterviewRequest {
  id: string;
  consultant_id: string;
  consultant_name: string;
  consultant_modules: string;
  consultant_experience: string;
  client_company: string;
  client_contact_name: string;
  client_email: string;
  client_phone: string;
  requested_interview_date: string | null;
  notes: string;
  admin_notes: string | null;
  admin_notes_created_at: string | null;
  resume_url: string | null;
  resume_file_name: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  workday_client_profiles?: {
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    title?: string;
    companies?: {
      name: string;
      city?: string;
      state?: string;
      country?: string;
    };
  };
  consultant_assignment?: {
    status: string;
    company_name?: string;
  };
}

export default function InterviewRequests() {
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const { theme, toggleTheme } = useWorkdayClientTheme();
  const [requests, setRequests] = useState<InterviewRequest[]>([]);
  const [filteredRequests, setFilteredRequests] = useState<InterviewRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedRequest, setExpandedRequest] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchInterviewRequests();

    const profilesSubscription = supabase
      .channel('client_profiles_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'workday_client_profiles' }, () => {
        fetchInterviewRequests();
      })
      .subscribe();

    const companiesSubscription = supabase
      .channel('client_companies_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'companies' }, () => {
        fetchInterviewRequests();
      })
      .subscribe();

    const interviewsSubscription = supabase
      .channel('client_interview_requests_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'interview_requests' }, () => {
        fetchInterviewRequests();
      })
      .subscribe();

    return () => {
      profilesSubscription.unsubscribe();
      companiesSubscription.unsubscribe();
      interviewsSubscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    filterRequests();
  }, [statusFilter, requests]);

  const fetchInterviewRequests = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        navigate('/customer/login');
        return;
      }

      const { data, error } = await supabase
        .from('interview_requests')
        .select(`
          *,
          workday_client_profiles!interview_requests_client_id_fkey (
            first_name,
            last_name,
            email,
            phone,
            title,
            companies (
              name,
              city,
              state,
              country
            )
          )
        `)
        .eq('client_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      if (!data || data.length === 0) {
        setRequests([]);
        setFilteredRequests([]);
        setLoading(false);
        return;
      }

      const consultantIds = data.map(r => r.consultant_id);

      const { data: assignments } = await supabase
        .from('consultant_assignments')
        .select(`
          consultant_id,
          status,
          companies (
            name
          )
        `)
        .in('consultant_id', consultantIds)
        .eq('status', 'active');

      const enrichedRequests = data.map(request => {
        const assignment = assignments?.find(a => a.consultant_id === request.consultant_id);
        return {
          ...request,
          consultant_assignment: assignment ? {
            status: assignment.status,
            company_name: (assignment.companies as any)?.name
          } : undefined
        };
      });

      setRequests(enrichedRequests || []);
      setFilteredRequests(enrichedRequests || []);
    } catch (error) {
      console.error('Error fetching interview requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterRequests = () => {
    if (statusFilter === 'all') {
      setFilteredRequests(requests);
    } else {
      setFilteredRequests(requests.filter(r => r.status === statusFilter));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'approved':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${theme === 'dark' ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#40E0D0]"></div>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    await signOut();
    navigate('/customer/login');
  };

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'}`}>
      <WorkdayClientHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-8">
        <button
          onClick={() => navigate('/workday-client/dashboard')}
          className="mb-6 flex items-center text-[#40E0D0] hover:text-[#40E0D0]/80 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">Interview Requests</h1>
          <p className={`text-lg ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Manage your consultant interview requests
          </p>
        </div>

        <div className={`mb-6 p-4 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'} shadow-sm`}>
          <div className="flex items-center gap-4">
            <Filter className="w-5 h-5 text-[#40E0D0]" />
            <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'approved', 'rejected', 'completed'].map((status) => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    statusFilter === status
                      ? 'bg-[#40E0D0] text-black'
                      : theme === 'dark'
                      ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Showing {filteredRequests.length} of {requests.length} requests
          </p>
        </div>

        <div className="space-y-4">
          {filteredRequests.map((request) => (
            <div
              key={request.id}
              className={`rounded-lg p-6 transition-all ${
                theme === 'dark'
                  ? 'bg-gray-900 border border-gray-800'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-xl font-semibold">{request.consultant_name}</h3>
                    <span className={`text-xs px-3 py-1 rounded-full border ${getStatusColor(request.status)}`}>
                      {request.status === 'pending' ? 'Pending Interview Through Perplexity' : request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                    </span>
                    {request.consultant_assignment && (
                      <span className="text-xs px-3 py-1 rounded-full border bg-blue-100 text-blue-800 border-blue-200">
                        On Assignment{request.consultant_assignment.company_name ? ` - ${request.consultant_assignment.company_name}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-4 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#40E0D0]" />
                      <span>{request.consultant_modules}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-[#40E0D0]" />
                      <span>{request.consultant_experience}</span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setExpandedRequest(expandedRequest === request.id ? null : request.id)}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark' ? 'hover:bg-gray-800' : 'hover:bg-gray-100'
                  }`}
                >
                  {expandedRequest === request.id ? (
                    <ChevronUp className="w-5 h-5" />
                  ) : (
                    <ChevronDown className="w-5 h-5" />
                  )}
                </button>
              </div>

              <div className="flex flex-wrap gap-4 text-sm mb-4">
                <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Requested: {formatDate(request.created_at)}
                </div>
                <div className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                  Last Updated: {formatDate(request.updated_at)}
                </div>
              </div>

              {expandedRequest === request.id && (
                <div className={`mt-4 pt-4 border-t ${theme === 'dark' ? 'border-gray-800' : 'border-gray-200'}`}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Building className="w-4 h-4 text-[#40E0D0]" />
                        Company Information
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Company: </span>
                          <span>{request.workday_client_profiles?.companies?.name || request.client_company}</span>
                        </div>
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Contact: </span>
                          <span>
                            {request.workday_client_profiles
                              ? `${request.workday_client_profiles.first_name} ${request.workday_client_profiles.last_name}`
                              : request.client_contact_name}
                          </span>
                        </div>
                        {request.workday_client_profiles?.title && (
                          <div>
                            <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Title: </span>
                            <span>{request.workday_client_profiles.title}</span>
                          </div>
                        )}
                        {request.workday_client_profiles?.companies && (
                          <div>
                            <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Location: </span>
                            <span>
                              {[
                                request.workday_client_profiles.companies.city,
                                request.workday_client_profiles.companies.state,
                                request.workday_client_profiles.companies.country
                              ].filter(Boolean).join(', ')}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <Mail className="w-3 h-3 text-[#40E0D0]" />
                          <span>{request.workday_client_profiles?.email || request.client_email}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Phone className="w-3 h-3 text-[#40E0D0]" />
                          <span>{request.workday_client_profiles?.phone || request.client_phone}</span>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-[#40E0D0]" />
                        Interview Preferences
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div>
                          <span className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Date: </span>
                          <span className={`${theme === 'dark' ? 'text-white' : 'text-black'}`}>{request.requested_interview_date ? new Date(request.requested_interview_date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-3 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-[#40E0D0]" />
                      Your Request Notes
                    </h4>
                    <div
                      className={`p-4 rounded-lg text-sm whitespace-pre-wrap ${
                        theme === 'dark' ? 'bg-gray-800 text-white' : 'bg-gray-50 text-black'
                      }`}
                    >
                      {request.notes}
                    </div>
                  </div>

                  {request.admin_notes && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <User className="w-4 h-4 text-[#40E0D0]" />
                        Admin Response
                      </h4>
                      <div
                        className={`p-4 rounded-lg text-sm whitespace-pre-wrap border-l-4 ${
                          request.status === 'approved'
                            ? theme === 'dark'
                              ? 'bg-green-900/20 border-green-500 text-white'
                              : 'bg-green-50 border-green-500 text-black'
                            : request.status === 'rejected'
                            ? theme === 'dark'
                              ? 'bg-red-900/20 border-red-500 text-white'
                              : 'bg-red-50 border-red-500 text-black'
                            : theme === 'dark'
                            ? 'bg-blue-900/20 border-blue-500 text-white'
                            : 'bg-blue-50 border-blue-500 text-black'
                        }`}
                      >
                        <p className="mb-2">{request.admin_notes}</p>
                        {request.admin_notes_created_at && (
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                            Response sent: {formatDate(request.admin_notes_created_at)}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {request.resume_url && request.resume_file_name && (
                    <div className="mt-4">
                      <h4 className="font-semibold mb-3 flex items-center gap-2">
                        <FileText className="w-4 h-4 text-[#40E0D0]" />
                        Consultant Resume
                      </h4>
                      <a
                        href={request.resume_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center justify-between p-4 rounded-lg border transition-colors ${
                          theme === 'dark'
                            ? 'bg-gray-800 border-gray-700 hover:bg-gray-700'
                            : 'bg-white border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-lg ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <FileText className="w-5 h-5 text-[#40E0D0]" />
                          </div>
                          <div>
                            <p className="font-medium">{request.resume_file_name}</p>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                              Attached by admin
                            </p>
                          </div>
                        </div>
                        <Download className="w-5 h-5 text-[#40E0D0]" />
                      </a>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        {filteredRequests.length === 0 && (
          <div className={`text-center py-12 rounded-lg ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}>
            <User className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h3 className="text-xl font-semibold mb-2">No interview requests found</h3>
            <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
              {statusFilter === 'all'
                ? 'Start by browsing the Talent Pool and requesting interviews'
                : `No ${statusFilter} requests at this time`}
            </p>
            <button
              onClick={() => navigate('/workday-client/talent')}
              className="mt-4 px-6 py-2 bg-[#40E0D0] text-black rounded-lg hover:bg-[#40E0D0]/90 transition-colors font-medium"
            >
              Browse Talent Pool
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
